import React from 'react';
import { motion } from 'framer-motion';
import { Home, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface BreadcrumbsItem {
  label: string;
  href?: string;
  active?: boolean;
}


interface BreadcrumbsProps {
  items: BreadcrumbsItem[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const breadcrumbVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.nav 
      initial="hidden"
      animate="visible"
      variants={breadcrumbVariants}
      aria-label="Breadcrumb"
      className="mb-6 bg-gray-100 p-3 rounded-lg"
    >
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => (
          <React.Fragment key={item.label}>
            <motion.li 
              variants={itemVariants}
              className="flex items-center"
            >
              {index === 0 ? (
                <Link 
                  to={item.href || '/'} 
                  className={`
                    flex items-center 
                    ${item.active 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-500 hover:text-gray-700'
                    }
                  `}
                >
                  {index === 0 && <Home className="mr-2" size={18} />}
                  {item.label}
                </Link>
              ) : item.href ? (
                <Link 
                  to={item.href} 
                  className={`
                    ${item.active 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-500 hover:text-gray-700'
                    }
                  `}
                >
                  {item.label}
                </Link>
              ) : (
                <span 
                  className={`
                    ${item.active 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-500'
                    }
                  `}
                >
                  {item.label}
                </span>
              )}
            </motion.li>
            
            {/* Separator */}
            {index < items.length - 1 && (
              <motion.li 
                variants={itemVariants}
                className="text-gray-400"
              >
                <ChevronRight size={16} />
              </motion.li>
            )}
          </React.Fragment>
        ))}
      </ol>
    </motion.nav>
  );
};

export default Breadcrumbs